import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '@rd/core/services/currency/currency.service';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { TransactionDetailsFormatterService } from '@rd/core/services/transaction-details-formatter/transaction-details-formatter.service';
import { CASH_NUMBER_DIGITS_INFO } from '@rd/core/tokens/rd-lib.token';
import { EnrollmentHelper } from '@rd/helpers/enrollment.helper';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { orderItemFilter } from '@rd/helpers/order.helper';
import { PointsTransactionHelper } from '@rd/helpers/points-transaction.helper';
import { DynamicListItem } from '@rd/shared-legacy/components/dynamic-list/dynamic-list-item.type';
import {
  CashBackToAccountMembership,
  CashBackToCardMembership,
  CashBackToPlatformMembership,
  CashRedemptionOrderItem,
  CashRedemptionOrderItemData,
  LoyaltyProgram,
  LoyaltyProgramCategory,
  Order,
  OrderItemStatus,
  OrderItemType,
} from '@rd/types';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';

@Component({
  selector: 'rd-rcv2-success-detail',
  templateUrl: './success-detail.component.html',
  styleUrls: ['./success-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RcV2SuccessDetailComponent implements OnChanges {
  OrderItemStatus = OrderItemStatus;
  LoyaltyProgramCategory = LoyaltyProgramCategory;

  @HostBinding('class.rd-rcv2-success-detail') componentClass = true;

  @Input() redeemCashOrder: Order;
  @Input() redeemCashLoyaltyProgram: LoyaltyProgram | undefined;

  hasDiscount = false;

  redeemCashOrderItemData: CashRedemptionOrderItemData;

  constructor(
    private currencyService: CurrencyService,
    private loyaltyCurrencyService: LoyaltyCurrencyService,
    private numberService: NumberService,
    private pointsTransactionHelper: PointsTransactionHelper,
    private redeemCashSettingService: RedeemCashSettingService,
    private transactionDetailsFormatterService: TransactionDetailsFormatterService,
    private loyaltyProgramHelper: LoyaltyProgramHelper,
    private enrollmentHelper: EnrollmentHelper,
    private translateService: TranslateService,
    @Inject(CASH_NUMBER_DIGITS_INFO) private digitsInfo: string,
  ) {}

  ngOnChanges({ redeemCashOrder }: SimpleChanges): void {
    if (redeemCashOrder) {
      this.redeemCashOrderItemData = this.redeemCashOrder.orderItems.find(
        orderItemFilter<CashRedemptionOrderItem>(
          OrderItemType.CashRedemptionOrderItem,
        ),
      )!.attributes.data;

      this.hasDiscount =
        !!this.redeemCashOrderItemData.promoCode &&
        this.redeemCashOrder.pointsPaid < this.redeemCashOrder.pointsAmount;
    }
  }

  dateFormat(): string {
    return this.pointsTransactionHelper.longDateFormat();
  }

  get status(): OrderItemStatus {
    const failedStatus = [OrderItemStatus.Failed, OrderItemStatus.Cancelled];

    if (failedStatus.includes(this.redeemCashOrder.fulfillmentStatus)) {
      return OrderItemStatus.Failed;
    } else {
      return OrderItemStatus.Fulfilled;
    }
  }

  get shouldDisplayMembershipDetailsOnSuccessPage(): boolean {
    return this.redeemCashSettingService
      .shouldDisplayMembershipDetailsOnSuccessPage;
  }

  // Translation keys

  redeemAmountString(amount: number): string {
    const params = this.redeemAmountParams(amount);

    return this.translateService.instant(
      'redeem_cash_v2.success.redeem_amount',
      params,
    );
  }

  redeemAmountParams(amount: number): Record<string, string> {
    return {
      loyaltyCurrencyAmount: this.numberService.formatNumber(
        Number(`${amount}`),
      ),
      // @ts-ignore
      loyaltyCurrency: this.loyaltyCurrencyService.loyaltyCurrencySelected.name,
      cashAmount: this.currencyService.formatCurrency(
        this.redeemCashOrderItemData.amount,
        'symbol',
        this.digitsInfo,
      ),
    };
  }

  get redeemToAccountDetails(): DynamicListItem[] {
    return this.transactionDetailsFormatterService.redeemCashToAccountDetails(
      this.redeemCashOrderItemData.membership as CashBackToAccountMembership,
    );
  }

  get redeemToCardDetails(): DynamicListItem[] {
    return this.transactionDetailsFormatterService.redeemCashToCardDetails(
      this.redeemCashOrderItemData.membership as CashBackToCardMembership,
    );
  }

  get redeemToPlatformDetails(): DynamicListItem[] {
    return this.transactionDetailsFormatterService.redeemCashToPlatformDetails(
      this.redeemCashOrderItemData.membership as CashBackToPlatformMembership,
    );
  }

  get processingDelay(): string {
    return this.redeemCashLoyaltyProgram
      ? this.loyaltyProgramHelper.transferTime(
          this.loyaltyProgramHelper.getCashBackCapability(
            this.redeemCashLoyaltyProgram,
          ).processingDelay,
        )
      : '';
  }

  get bsbNumber(): string {
    return (
      this.redeemCashOrderItemData.membership as CashBackToAccountMembership
    ).details.bsbNumber;
  }

  selectedEnrollmentAccountAriaLabelInConfirmationPage(
    orderData: CashRedemptionOrderItemData,
  ): string {
    return this.enrollmentHelper.selectedEnrollmentAccountAriaLabelInConfirmationPage(
      orderData,
    );
  }
}
